import { urls } from "@selector/shared";
import { selMessage } from "@selector/shared";
import { TOKEN_KEY } from "@selector/shared/settings";
import { type FC, type PropsWithChildren, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { ActionButton, ActionButtonIntent, Callout, CalloutIntent, Card, Logos, VStack } from "widgets";
import { storage } from "widgets";
import { useURLSearchParam } from "widgets/hooks";

import { LoginForm, type LoginFormData } from "@/modules/users/components/LoginForm.tsx";

import { getDefaultBrowserTitle } from "../../app-utils/dom.js";
import { useDocumentTitle } from "../../app-utils/hooks/useDocumentTitle.js";
import { deleteTokenAndLogout, loginUser, loginUserToken, logoutInternal } from "../../redux/modules/auth.js";
import type { IAppState } from "../../redux/reducer.js";
import type { ReduxDispatch } from "../../redux/redux-utils.js";
import { defaultMapOfAcronyms, EXTERNAL_LOGIN_TOKEN } from "../../settings/settings.js";
import { HelpMenu } from "./navbar/NavBarMenus.js";

function getCookie(cName: string): string | undefined {
  const name = `${cName}=`;
  const cDecoded = decodeURIComponent(document.cookie); // to be careful
  const cArr = cDecoded.split("; ");
  let res: string | undefined;
  for (const val of cArr) {
    if (val.startsWith(name)) res = val.slice(name.length);
  }
  return res;
}

export function NotAllowed() {
  const navigate = useNavigate();
  return (
    <WelcomingPage>
      <Callout
        title="You are not allowed to use this application"
        intent={CalloutIntent.Warning}
        style={{ marginTop: 5 }}
      >
        Please contact your Selector administrator.
      </Callout>
      <ActionButton label="Login" onPress={() => navigate("/logout")} intent={ActionButtonIntent.Outlined} />
    </WelcomingPage>
  );
}

// => ?!?
export function Welcome() {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();
  const appName = useSelector<IAppState, string>(
    (state) => getDefaultBrowserTitle(state.config.configuration, true),
    shallowEqual,
  );

  useEffect(() => {
    // make sure the navbar does not appear
    dispatch(logoutInternal());
    dispatch(deleteTokenAndLogout());
  }, []);

  return (
    <WelcomingPage>
      <Callout title={`Welcome to ${appName}`} intent={CalloutIntent.None} style={{ marginTop: 5 }}>
        Click the &apos;Login&apos; button to start.
      </Callout>
      <ActionButton label="Login" onPress={() => navigate("/login")} intent={ActionButtonIntent.Outlined} />
    </WelcomingPage>
  );
}

export function LoginPageExtern() {
  let redirectTo: string | null = useURLSearchParam("redirect");
  redirectTo = redirectTo ? decodeURIComponent(redirectTo) : "/";

  const dispatch = useDispatch<ReduxDispatch>();
  const ref = window.location.href;
  const token: string | undefined = getCookie(EXTERNAL_LOGIN_TOKEN);
  const key = storage[TOKEN_KEY];

  if (!!token && !key) {
    // We got a token from the MT, i.e. we are authenticated by the IDP only.
    // Proceed to the authorization...
    void dispatch(loginUserToken(token, redirectTo));
    return null;
  }

  if (!!token && !!key) {
    return null;
  }

  window.location.href = `${urls.api}/SSOLogin?redirect=${ref.replaceAll(/\?.*/g, "")}`;
  return null;
}

export function LoginPage() {
  const dispatch = useDispatch<ReduxDispatch>();
  const redirectTo: string = useRedirectTo();
  const onSubmit = (formData: LoginFormData) => dispatch(loginUser(formData.username, formData.password, redirectTo));
  const appName = useSelector<IAppState, string>(
    (state) => getDefaultBrowserTitle(state.config.configuration, true),
    shallowEqual,
  );

  const contactHtml = useSelector<IAppState, string>(
    (state) =>
      state.config.configuration?.application?.contactHtml ??
      `You must have a valid and existing account for ${appName}.`,
    shallowEqual,
  );

  useDocumentTitle(defaultMapOfAcronyms.GENEDATA);

  return (
    <WelcomingPage>
      <LoginForm onSubmit={onSubmit} />
      <Callout intent={"_" as CalloutIntent}>
        <div dangerouslySetInnerHTML={{ __html: contactHtml }} />
      </Callout>
    </WelcomingPage>
  );
}

function useRedirectTo() {
  let redirectTo: string | null = useURLSearchParam("redirect");

  redirectTo = redirectTo == null ? "/" : decodeURIComponent(redirectTo);

  return redirectTo;
}

export const WelcomingPage: FC<PropsWithChildren<{ title?: string }>> = (props) => {
  const appName = useSelector<IAppState, string>(
    (state) => getDefaultBrowserTitle(state.config.configuration, true),
    shallowEqual,
  );
  return (
    <div
      style={{
        gridArea: "page",
        background: "#D8E4ED",
        alignItems: "flex-start",
        overflow: "auto",
        textAlign: "center",
      }}
    >
      <VStack spacing={70} style={{ alignItems: "center", marginTop: 70 }}>
        <img src={Logos.GenedataSelector} alt={appName} width="280" aria-hidden="true" />
        <Card
          icon={
            <img
              src={Logos.SelectorLogo}
              alt={appName}
              width="24"
              aria-hidden="true"
              style={{ display: "block", marginLeft: 5, borderRadius: 3 }}
            />
          }
          title={props.title ?? selMessage({ id: "login.welcome" })}
          actions={[<HelpMenu key="login-page-menu" />]}
          style={{ width: 300, borderColor: "#7494b7", marginBottom: 20 }}
        >
          {props.children}
        </Card>
      </VStack>
    </div>
  );
};
